<script setup lang="ts">
// The right-hand panel of the modal: header, one card per deactivated chamber,
// the count line and the field-size-labels toggle. Hover/selection are shared
// with the graph via v-models.
import { mainNavigationQaEquipment } from '@/components/icons/icons';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import type { DeactivatedChamber } from './types';

type DeactivatedChamberModalListProps = {
  chambers: DeactivatedChamber[];
  // ids known to the detector layout; chambers outside it are shown dimmed
  // and non-interactive (there is no dot to highlight or zoom to)
  layoutChamberIds: Set<number>;
  totalChambers: number;
  model?: string;
};
const props = defineProps<DeactivatedChamberModalListProps>();

const { t } = useTranslation();

// e.g. "STARCHECK — Array detector"; degrades to just the category when the
// device model is missing
const deviceSubtitle = computed(() => {
  const category = t('QaEquipmentSubCategoryEnum.ArrayDetector');
  return props.model ? `${props.model} — ${category}` : category;
});

const hoveredId = defineModel<number | undefined>('hoveredId');
const selectedChamberId = defineModel<number | undefined>('selectedChamberId');
const showFrameLabels = defineModel<boolean>('showFrameLabels', { default: false });
</script>

<template>
  <div class="chambers-list">
    <div class="d-flex align-center mb-1">
      <v-icon size="28" :icon="mainNavigationQaEquipment" class="mr-2" />
      <h3 class="mb-0">{{ $t('DeactivatedChambers') }}</h3>
    </div>
    <p class="text-body-2 mb-4 chambers-subtitle">
      {{ deviceSubtitle }}
    </p>
    <p class="text-body-2 mb-4 chambers-subtitle">
      {{ $t('DeactivatedChambersExcludedMsg') }}
    </p>
    <div
      v-for="(chamber, index) in chambers"
      :key="chamber.id"
      class="chamber-item"
      :class="[
        chamber.group === 'DC' ? 'group-dc' : 'group-uc',
        {
          hovered: hoveredId === chamber.id,
          selected: selectedChamberId === chamber.id,
          'not-on-layout': !layoutChamberIds.has(chamber.id),
        },
      ]"
      :style="{ animationDelay: `${150 + index * 80}ms` }"
      @mouseenter="layoutChamberIds.has(chamber.id) && (hoveredId = chamber.id)"
      @mouseleave="hoveredId = undefined"
      @click="layoutChamberIds.has(chamber.id) && (selectedChamberId = chamber.id)"
    >
      <span class="chamber-dot" />
      <div>
        <div class="font-weight-bold">{{ $t('ChamberWithNumber', { number: chamber.id }) }}</div>
        <div class="text-body-2 chambers-subtitle">
          {{ chamber.group === 'DC' ? $t('DeactivatedByPtw') : $t('DeactivatedByUser') }}
        </div>
        <div v-if="!layoutChamberIds.has(chamber.id)" class="text-body-2 chambers-subtitle">
          {{ $t('NotOnDetectorLayout') }}
        </div>
      </div>
      <v-chip
        class="ml-auto"
        label
        size="small"
        :color="chamber.group === 'DC' ? 'warning' : 'info'"
        :text="chamber.group"
      />
    </div>
    <div class="chamber-count text-body-2">
      {{ $t('ChambersDeactivatedCount', { deactivated: chambers.length, total: totalChambers }) }}
    </div>
    <v-switch
      v-model="showFrameLabels"
      class="labels-toggle"
      :label="$t('ShowFieldSizeLabels')"
      color="surface"
      density="compact"
      hideDetails
    />
  </div>
</template>

<style scoped lang="scss">
// DC (PTW) = orange, UC (user) = blue; triplet so solid fills and translucent
// glows share one theme color
.group-dc {
  --group-rgb: var(--v-theme-warning);
}
.group-uc {
  --group-rgb: var(--v-theme-info);
}

@keyframes chamber-pulse {
  0%,
  100% {
    filter: drop-shadow(0 0 0.5px rgba(var(--group-rgb), 0.4));
  }
  50% {
    filter: drop-shadow(0 0 4px rgba(var(--group-rgb), 0.9));
  }
}

.chambers-subtitle {
  opacity: 0.75;
}

.chambers-list {
  flex: 1;
  min-width: 280px;
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  padding-right: 8px;
  // mounts with phase 2: soft fade-in alongside the background
  animation: item-enter 0.4s ease;

  // mobile: the card owns the scroll, so the list flows naturally below the
  // graph rather than being its own scroll container
  @media (max-width: 1024px) {
    min-width: 0;
    max-height: none;
    overflow: visible;
  }
}

.chamber-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid rgb(var(--v-theme-primary-lighten-4));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
  // items keep their light fill, so their text stays dark on the dark card
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  opacity: 0;
  animation: item-enter 0.4s ease forwards;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease;

  // hover/selected: group tint layered over the surface fill
  &.hovered {
    border-color: rgb(var(--group-rgb));
    background: linear-gradient(rgba(var(--group-rgb), 0.04), rgba(var(--group-rgb), 0.04))
      rgb(var(--v-theme-surface));
    transform: translateX(4px);
  }

  &.selected {
    border-color: rgb(var(--group-rgb));
    border-width: 2px;
    background: linear-gradient(rgba(var(--group-rgb), 0.08), rgba(var(--group-rgb), 0.08))
      rgb(var(--v-theme-surface));
    transform: translateX(4px);
  }

  &.not-on-layout {
    --item-final-opacity: 0.55;
    border-style: dashed;
    cursor: default;
  }
}

// ends at a variable opacity: not-on-layout items stay dimmed even though the
// animation's fill-forwards would otherwise pin opacity at the last keyframe
@keyframes item-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: var(--item-final-opacity, 1);
    transform: translateY(0);
  }
}

.chamber-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  // inherits --group-rgb from the surrounding .chamber-item's group class
  background: rgb(var(--group-rgb));
  animation: chamber-pulse 1.6s ease-in-out infinite;
  flex-shrink: 0;
}

.chamber-count {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed rgb(var(--v-theme-primary-lighten-4));
  opacity: 0.7;
}

.labels-toggle {
  // the scrollable list clips horizontally; keep the toggle clear of the edge
  padding-left: 8px;

  :deep(.v-label) {
    color: #fff;
    opacity: 1;
  }
}
</style>
